import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { apiTokensService } from '../../services/apiTokens';
import type { ApiToken, CreateApiTokenData } from '../../types/apiToken';

// ─── Scope definitions ────────────────────────────────────────────────────────

function useAvailableScopes() {
  const { t } = useTranslation('common');
  return [
    { value: 'campaigns:read',  label: t('api_keys.scopes.campaigns_read_label'),  description: t('api_keys.scopes.campaigns_read_desc') },
    { value: 'campaigns:write', label: t('api_keys.scopes.campaigns_write_label'), description: t('api_keys.scopes.campaigns_write_desc') },
    { value: 'statistics:read', label: t('api_keys.scopes.statistics_read_label'), description: t('api_keys.scopes.statistics_read_desc') },
    { value: 'team:write',      label: t('api_keys.scopes.team_write_label'),      description: t('api_keys.scopes.team_write_desc') },
  ];
}

const SCOPE_BADGE_COLORS: Record<string, string> = {
  'campaigns:read':  'bg-blue-100 text-blue-800',
  'campaigns:write': 'bg-green-100 text-green-800',
  'statistics:read': 'bg-amber-100 text-amber-800',
  'team:write':      'bg-rose-100 text-rose-800',
};

// ─── Helper components ────────────────────────────────────────────────────────

function ScopeBadge({ scope }: { scope: string }) {
  const colorClass = SCOPE_BADGE_COLORS[scope] ?? 'bg-gray-100 text-gray-800';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {scope}
    </span>
  );
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return '—';
  }
}

// ─── Generate Key Modal ───────────────────────────────────────────────────────

interface GenerateModalProps {
  onClose: () => void;
  onSuccess: (plainToken: string, tokenName: string) => void;
}

function GenerateModal({ onClose, onSuccess }: GenerateModalProps) {
  const { t } = useTranslation('common');
  const availableScopes = useAvailableScopes();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [nameError, setNameError] = useState('');
  const [scopeError, setScopeError] = useState('');

  const { mutate: createToken, isPending } = useMutation({
    mutationFn: (data: CreateApiTokenData) => apiTokensService.createApiToken(data),
    onSuccess: (result) => {
      onSuccess(result.plain_token, result.data.name);
    },
    onError: (err: Error) => {
      toast.error(err.message || t('api_keys.error_create'));
    },
  });

  const handleToggleScope = (scope: string) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
    setScopeError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let valid = true;

    if (!name.trim()) {
      setNameError(t('api_keys.generate_modal.name_required'));
      valid = false;
    }
    if (scopes.length === 0) {
      setScopeError(t('api_keys.generate_modal.scope_required'));
      valid = false;
    }
    if (!valid) return;

    createToken({ name: name.trim(), scopes, expires_at: expiresAt || null });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">{t('api_keys.generate_modal.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label htmlFor="token-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('api_keys.generate_modal.name_label')} <span className="text-red-500">*</span>
            </label>
            <input
              id="token-name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(''); }}
              placeholder={t('api_keys.generate_modal.name_placeholder')}
              className={`input w-full ${nameError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
            />
            {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
          </div>

          {/* Scopes */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">
              {t('api_keys.generate_modal.permissions_label')} <span className="text-red-500">*</span>
            </span>
            <div className="space-y-2">
              {availableScopes.map((scope) => (
                <label
                  key={scope.value}
                  className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope.value)}
                    onChange={() => handleToggleScope(scope.value)}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{scope.label}</p>
                    <p className="text-xs text-gray-500">{scope.description}</p>
                  </div>
                </label>
              ))}
            </div>
            {scopeError && <p className="mt-1 text-xs text-red-600">{scopeError}</p>}
          </div>

          {/* Expiry date */}
          <div>
            <label htmlFor="token-expires" className="block text-sm font-medium text-gray-700 mb-1">
              {t('api_keys.generate_modal.expiry_label')}{' '}
              <span className="text-gray-400">{t('api_keys.generate_modal.expiry_optional')}</span>
            </label>
            <input
              id="token-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="input w-full"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={isPending}>
              {t('api_keys.generate_modal.cancel')}
            </button>
            <button type="submit" disabled={isPending} className="btn btn-primary flex-1">
              {isPending ? t('api_keys.generate_modal.generating') : t('api_keys.generate_modal.generate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Token Reveal Modal ───────────────────────────────────────────────────────

interface TokenRevealModalProps {
  plainToken: string;
  tokenName: string;
  onDone: () => void;
}

function TokenRevealModal({ plainToken, tokenName, onDone }: TokenRevealModalProps) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-600 bg-opacity-75" />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        {/* Warning banner */}
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
          <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-800">{t('api_keys.reveal_modal.warning_title')}</p>
            <p className="text-sm text-amber-700 mt-0.5">{t('api_keys.reveal_modal.warning_body')}</p>
          </div>
        </div>

        <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('api_keys.reveal_modal.title')}</h2>
        <p className="text-sm text-gray-500 mb-4">
          {t('api_keys.reveal_modal.reveal_subtitle_before')}
          <span className="font-medium text-gray-700">{tokenName}</span>
          {t('api_keys.reveal_modal.reveal_subtitle_after')}
        </p>

        {/* Token display */}
        <div className="relative mb-5">
          <div className="bg-gray-900 rounded-lg p-4 pr-12 font-mono text-sm text-gray-100 break-all">
            {plainToken}
          </div>
          <button
            onClick={handleCopy}
            title={t('api_keys.reveal_modal.copy_tooltip')}
            className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
          >
            {copied ? (
              <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        </div>

        {/* Confirmation checkbox */}
        <label className="flex items-center gap-3 mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">{t('api_keys.reveal_modal.confirm_checkbox')}</span>
        </label>

        <button
          onClick={onDone}
          disabled={!confirmed}
          className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('api_keys.reveal_modal.done')}
        </button>
      </div>
    </div>
  );
}

// ─── Inline Revoke Confirmation ───────────────────────────────────────────────

interface RevokeConfirmProps {
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function RevokeConfirm({ onConfirm, onCancel, isPending }: RevokeConfirmProps) {
  const { t } = useTranslation('common');
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-sm text-gray-600">{t('api_keys.revoke.confirm')}</span>
      <button onClick={onConfirm} disabled={isPending} className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50">
        {isPending ? t('api_keys.revoke.revoking') : t('api_keys.revoke.yes')}
      </button>
      <button onClick={onCancel} disabled={isPending} className="text-sm font-medium text-gray-500 hover:text-gray-700">
        {t('api_keys.revoke.no')}
      </button>
    </span>
  );
}

// ─── Token Row ────────────────────────────────────────────────────────────────

interface TokenRowProps {
  token: ApiToken;
}

function TokenRow({ token }: TokenRowProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const { mutate: revoke, isPending } = useMutation({
    mutationFn: () => apiTokensService.revokeApiToken(token.id),
    onSuccess: () => {
      toast.success(t('api_keys.revoke.success'));
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || t('api_keys.revoke.error'));
      setConfirming(false);
    },
  });

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{token.name}</div>
        <div className="text-xs text-gray-500 font-mono mt-0.5">{token.token_prefix}...</div>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {token.scopes.map((scope) => (
            <ScopeBadge key={scope} scope={scope} />
          ))}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {token.last_used_at ? formatDate(token.last_used_at) : t('api_keys.table.never')}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(token.created_at)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {token.expires_at ? formatDate(token.expires_at) : t('api_keys.table.never')}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        {confirming ? (
          <RevokeConfirm onConfirm={() => revoke()} onCancel={() => setConfirming(false)} isPending={isPending} />
        ) : (
          <button onClick={() => setConfirming(true)} className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors">
            {t('api_keys.revoke.button')}
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ onGenerate }: { onGenerate: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="text-center py-16 px-6">
      <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
      </div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('api_keys.empty_state.title')}</h3>
      <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">{t('api_keys.empty_state.description')}</p>
      <button onClick={onGenerate} className="btn btn-primary">
        {t('api_keys.empty_state.button')}
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ApiKeysPage() {
  const { t } = useTranslation('common');
  const [showGenerate, setShowGenerate] = useState(false);
  const [revealedToken, setRevealedToken] = useState<{ plain: string; name: string } | null>(null);
  const queryClient = useQueryClient();

  const { data: tokens = [], isLoading, isError } = useQuery({
    queryKey: ['api-tokens'],
    queryFn: () => apiTokensService.listApiTokens(),
  });

  const handleGenerateSuccess = (plainToken: string, tokenName: string) => {
    setShowGenerate(false);
    setRevealedToken({ plain: plainToken, name: tokenName });
    queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
  };

  return (
    <>
      {showGenerate && (
        <GenerateModal onClose={() => setShowGenerate(false)} onSuccess={handleGenerateSuccess} />
      )}

      {revealedToken && (
        <TokenRevealModal
          plainToken={revealedToken.plain}
          tokenName={revealedToken.name}
          onDone={() => setRevealedToken(null)}
        />
      )}

      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('api_keys.page_title')}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('api_keys.page_subtitle')}</p>
          </div>
          <button onClick={() => setShowGenerate(true)} className="btn btn-primary">
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('api_keys.generate_button')}
          </button>
        </div>

        {/* Keys table card */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : isError ? (
            <div className="text-center py-16 text-sm text-red-600">
              {t('api_keys.error_load')}
            </div>
          ) : tokens.length === 0 ? (
            <EmptyState onGenerate={() => setShowGenerate(true)} />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('api_keys.table.name_prefix')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('api_keys.table.scopes')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('api_keys.table.last_used')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('api_keys.table.created')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('api_keys.table.expires')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('api_keys.table.action')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tokens.map((token) => (
                    <TokenRow key={token.id} token={token} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info card */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
          <svg className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-700">
            {t('api_keys.info_banner_before')}
            <a href="/api-docs" className="font-medium underline hover:no-underline">{t('api_keys.info_banner_link')}</a>
            {t('api_keys.info_banner_after')}
          </p>
        </div>
      </div>
    </>
  );
}
